import React, { useState, useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Loader, Button, Alert } from '@refera/ui-web'
import * as Modal from '_components/modal/generic-modal'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { InputLabel, TextField, IconButton } from '@material-ui/core'
import { Trash } from '@refera/ui-icons'
import { EditOutlined } from '@material-ui/icons'
import { FormProvider, useForm, Controller, useFieldArray } from 'react-hook-form'

import { requestReferaProviders } from '_modules/provider/actions'
import { getSortedCitiesOptionsSelector } from '_modules/utils/selectors'
import { categoriesSelector } from '_modules/categories/selectors'
import AddCategoryModal from '_/components/modal/add-category-modal'

import { useToast } from '_/hooks/use-toast'
import { useDialog } from '_/hooks/use-dialog'
import useStyles from './styles'

const DEFAULT_VALUES = { providers: [{ city: null, categories: [] }] }

export function ProviderSolicitationModal({ isOpen, setIsOpen }) {
  const styles = useStyles()
  const dispatch = useDispatch()
  const cityOptions = useSelector(getSortedCitiesOptionsSelector)
  const [isLoading, setIsLoading] = useState(false)
  const categoryList = useSelector(categoriesSelector)
  const { showDialog, closeDialog } = useDialog()
  const { showToast } = useToast()

  const [activeModalIndex, setActiveModalIndex] = useState(null)
  const [visible, setVisible] = useState(true)
  const toggleModal = index => setActiveModalIndex(index)

  const methods = useForm({
    mode: 'all',
    defaultValues: DEFAULT_VALUES,
  })
  const {
    control,
    formState: { isValid },
    handleSubmit,
  } = methods

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'providers',
  })

  const handleAppendItem = () => {
    append({ city: null, categories: [] })
  }

  function handleCancel() {
    const isActuallyDirty = JSON.stringify(methods.getValues()) !== JSON.stringify(DEFAULT_VALUES)
    if (isActuallyDirty) {
      showDialog({
        type: 'warning',
        subject: 'Você tem certeza que deseja cancelar estas alterações?',
        labelApprove: 'Sim',
        labelCancel: 'Não',
        onApprove: () => {
          closeDialog()
          setIsOpen(false)
        },
        onCancel: closeDialog,
      })
    } else {
      setIsOpen(false)
    }
  }

  function handleRemove(index) {
    showDialog({
      type: 'warning',
      subject: 'Você tem certeza que deseja executar esta ação?',
      onApprove: () => {
        remove(index)
        closeDialog()
      },
      onCancel: closeDialog,
      labelApprove: 'Confirmar',
      labelCancel: 'Cancelar',
    })
    return null
  }

  const getCategoryNames = useCallback(categories => {
    if (!Array.isArray(categories) || categories?.length === 0) return ''
    return categories?.map(cat => cat?.name).join(', ')
  }, [])

  const mapIdsToCategories = useCallback(
    ids => {
      const nameById = new Map(categoryList.map(cat => [cat.id, cat.name]))
      return ids.map(id => ({
        id,
        name: nameById.get(id),
      }))
    },
    [categoryList]
  )

  const handlePatchCategories = useCallback(
    (index, ids) => {
      const categoriesWithNames = mapIdsToCategories(ids)
      methods.setValue(`providers[${index}].categories`, categoriesWithNames, {
        shouldValidate: true,
        shouldDirty: true,
      })
    },
    [methods, mapIdsToCategories]
  )

  const onSubmit = useCallback(
    data => {
      setIsLoading(true)
      dispatch(requestReferaProviders(data))
        .then(() => {
          showToast({ type: 'success' })

          setIsOpen(false)
        })
        .catch(() => {
          showToast({ type: 'error' })
        })
        .finally(() => setIsLoading(false))
    },
    [dispatch, showToast, setIsOpen]
  )

  useEffect(() => {
    methods.reset(DEFAULT_VALUES, { keepDirty: false })
  }, [isOpen])

  return (
    <>
      <Loader hasBackdrop open={isLoading} />
      <Modal.Root className={styles.root} open={isOpen} onClose={() => setIsOpen(false)}>
        <Modal.TitleModal title="Solicitar Prestador" />

        <Modal.Content className={styles.dialogContent}>
          {visible && (
            <Alert
              severity="info"
              title="Solicitação de Prestador"
              onClose={() => setVisible(false)}
            >
              Cada solicitação equivale a UM prestador da categoria (ou categorias) selecionada.
              Caso deseje solicitar mais de um prestador para determinada categoria, clique em
              &quot;Adicionar Item&quot; e faça mais uma solicitação. Para saber como fazer isso,{' '}
              <a
                style={{ color: '#1671ed', textDecoration: 'none' }}
                href="https://www.youtube.com/watch?v=_9Zuv218emg"
                target="_blank"
                rel="noopener noreferrer"
              >
                clique aqui
              </a>
              .
            </Alert>
          )}
          <form
            id="provider-solicitation"
            className={styles.formContainer}
            onSubmit={handleSubmit(onSubmit)}
          >
            <FormProvider {...methods}>
              {fields?.map((f, index) => {
                return (
                  <div key={f.id} className={styles.rowContainer}>
                    <div className={styles.textAreaContainer}>
                      <InputLabel variant="standard" className={styles.textAreaLabel}>
                        Cidade <span className={styles.asterisk}>*</span>
                      </InputLabel>
                      <Controller
                        control={control}
                        name={`providers[${index}].city`}
                        rules={{ required: 'Esse campo é obrigatório' }}
                        render={({ name, onChange, onBlur }) => (
                          <Autocomplete
                            id={name}
                            options={cityOptions}
                            multiple={false}
                            getOptionLabel={option => option?.label ?? ''}
                            isOptionEqualToValue={(option, value) => option?.value === value?.value}
                            onChange={(_, newValue) => onChange(newValue)}
                            renderInput={params => (
                              <TextField
                                {...params}
                                onBlur={onBlur}
                                error={!!methods.errors?.providers?.[index]?.city}
                                helperText={methods.errors?.providers?.[index]?.city?.message}
                              />
                            )}
                            style={{ flex: 1 }}
                          />
                        )}
                      />
                    </div>

                    <div className={styles.categoriesWrapper}>
                      <div className={styles.textAreaContainer}>
                        <InputLabel variant="standard" className={styles.textAreaLabel}>
                          Categorias <span className={styles.asterisk}>*</span>
                        </InputLabel>

                        <Controller
                          name={`providers[${index}].categories`}
                          control={methods.control}
                          rules={{
                            validate: value =>
                              Array.isArray(value) && value.length > 0
                                ? true
                                : 'Esse campo é obrigatório',
                          }}
                          render={({ value, onChange, onBlur }) => (
                            <TextField
                              disabled
                              className={[styles.textArea, styles.thinErrorUnderline]}
                              value={getCategoryNames(value)}
                              onChange={onChange}
                              onBlur={onBlur}
                              multiline
                              variant="outlined"
                              maxRows={15}
                              minRows={2}
                              error={!!methods.errors?.providers?.[index]?.categories}
                              helperText={methods.errors?.providers?.[index]?.categories?.message}
                            />
                          )}
                        />
                      </div>
                      <div>
                        <IconButton
                          variant="ghost"
                          className={styles.iconButton}
                          onClick={() => toggleModal(index)}
                        >
                          <EditOutlined style={{ color: '#1671ED' }} className={styles.editIcon} />
                        </IconButton>
                        <IconButton
                          variant="ghost"
                          className={styles.iconButton}
                          onClick={() => handleRemove(index)}
                        >
                          <Trash color="red" />
                        </IconButton>
                      </div>
                    </div>
                  </div>
                )
              })}
              <Button
                variant="secondary"
                onClick={handleAppendItem}
                style={{ width: '160px', marginTop: '14px' }}
              >
                + Adicionar item
              </Button>
            </FormProvider>
          </form>
        </Modal.Content>
        <Modal.Actions>
          <Modal.ButtonRed onClick={handleCancel}>Cancelar</Modal.ButtonRed>
          <Modal.ButtonFullBlue
            type="submit"
            form="provider-solicitation"
            disabled={!isValid || isLoading}
          >
            Enviar
          </Modal.ButtonFullBlue>
        </Modal.Actions>
      </Modal.Root>
      {activeModalIndex !== null && (
        <AddCategoryModal
          value={methods.watch(`providers[${activeModalIndex}].categories`)?.map(c => c?.id) ?? []}
          patchCategories={ids => handlePatchCategories(activeModalIndex, ids)}
          handleModal={() => setActiveModalIndex(null)}
          title="Categorias"
          tradesmanCategories={
            methods.watch(`providers[${activeModalIndex}].categories`)?.map(c => c?.id) ?? []
          }
          showChip={false}
        />
      )}
    </>
  )
}
